import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, ApiError } from '../../api';
import { store } from '../../store';
import { showToast } from '../../components/ui/toast';
import { t } from '../../i18n';

interface Tag { id: string; name: string; color: string; }
interface Named { id: string; name: string; }
interface MailingList { id: string; name: string; filters: Filters; created_by_name?: string | null; updated_at: string; }
interface Recipient { type: 'student' | 'contact'; id: string; name: string; email: string; subscribed: boolean; }
interface Preview { total: number; subscribed: number; sample: Recipient[] }
type Filters = {
  contact_type?: 'students' | 'contacts' | 'both';
  status?: string; tag_ids?: string[];
  class_id?: string; dance_type_id?: string; level_id?: string;
  age_min?: string | number; age_max?: string | number;
  payment_status?: string; event_id?: string;
};

const newFilters = (): Filters => ({ contact_type: 'students', tag_ids: [] });

/**
 * <cadence-mailing-lists> — the Mailing Lists tab. Build a named, dynamic list by
 * combining filters (AND), preview the resolved recipients live, and save for reuse.
 * Saved lists re-resolve from live data every time they're previewed/used.
 */
@customElement('cadence-mailing-lists')
export class CadenceMailingLists extends LitElement {
  @state() private lists: MailingList[] = [];
  @state() private loading = true;
  @state() private selectedId: string | null = null; // null = new, unsaved
  @state() private name = '';
  @state() private filters: Filters = newFilters();
  @state() private preview: Preview | null = null;
  @state() private previewing = false;
  @state() private saving = false;
  // Filter option sources
  @state() private tags: Tag[] = [];
  @state() private classes: Named[] = [];
  @state() private danceTypes: Named[] = [];
  @state() private levels: Named[] = [];
  @state() private events: Named[] = [];

  private readonly canManage = store.hasPermission('mailing.manage');
  private debounce = 0;

  static styles = css`
    :host { display: block; }
    .layout { display: grid; grid-template-columns: 260px 1fr; gap: var(--space-4); align-items: start; }
    @media (max-width: 820px) { .layout { grid-template-columns: 1fr; } }
    .lists { display: flex; flex-direction: column; gap: 6px; }
    .litem { text-align: left; background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: var(--radius-md); padding: 10px 12px; cursor: pointer; font: inherit; color: var(--color-text-primary); }
    .litem:hover { border-color: var(--color-primary); }
    .litem[aria-selected='true'] { border-color: var(--color-primary); background: color-mix(in srgb, var(--color-primary) 8%, transparent); }
    .litem .n { font-weight: 600; }
    .litem .m { color: var(--color-text-secondary); font-size: 0.78rem; }
    .builder { display: grid; gap: var(--space-4); }
    .fgrid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: var(--space-3); }
    .field label { display: block; font-size: 0.8rem; color: var(--color-text-secondary); margin-bottom: 4px; }
    .tagpick { display: flex; gap: 6px; flex-wrap: wrap; }
    .tagtoggle { cursor: pointer; background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: 999px; padding: 4px 12px; font: inherit; font-size: 0.82rem; color: var(--color-text-secondary); display: inline-flex; align-items: center; gap: 6px; }
    .tagtoggle .dot { width: 9px; height: 9px; border-radius: 50%; }
    .tagtoggle[aria-pressed='true'] { border-color: var(--color-primary); color: var(--color-primary); background: color-mix(in srgb, var(--color-primary) 8%, transparent); }
    .preview { border: 1px solid var(--color-border); border-radius: var(--radius-md); }
    .preview .ph { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; padding: 10px 14px; border-bottom: 1px solid var(--color-border); }
    .preview .ph .count { font-weight: 700; font-size: 1.05rem; }
    .preview .rows { max-height: 320px; overflow: auto; }
    .preview .r { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 7px 14px; border-bottom: 1px solid var(--color-border); font-size: 0.88rem; }
    .preview .r:last-child { border-bottom: none; }
    .preview .r .who { min-width: 0; }
    .preview .r .em { color: var(--color-text-secondary); font-size: 0.82rem; }
    .pill { font-size: 0.7rem; border-radius: 999px; padding: 1px 8px; border: 1px solid var(--color-border); color: var(--color-text-secondary); white-space: nowrap; }
    .pill.unsub { border-color: var(--color-danger); color: var(--color-danger); }
    .row { display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap; }
    .muted { color: var(--color-text-secondary); font-size: 0.85rem; }
    .actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
    .rm { cursor: pointer; color: var(--color-danger); background: none; border: none; font: inherit; }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    this.loading = true;
    try {
      this.lists = await api.get<MailingList[]>('/mailing/lists');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
    // Filter option sources — load defensively (an admin may lack a given section's view).
    this.tags = await api.get<Tag[]>('/tags').catch(() => []);
    this.classes = await api.get<Named[]>('/classes').catch(() => []);
    this.danceTypes = await api.get<Named[]>('/dance/types').catch(() => []);
    this.levels = await api.get<Named[]>('/dance/levels').catch(() => []);
    this.events = await api.get<Named[]>('/events').catch(() => []);
    this.runPreview();
  }

  private selectList(l: MailingList) {
    this.selectedId = l.id;
    this.name = l.name;
    this.filters = { contact_type: 'students', tag_ids: [], ...l.filters };
    this.runPreview();
  }
  private newList() {
    this.selectedId = null;
    this.name = '';
    this.filters = newFilters();
    this.runPreview();
  }

  private setF(patch: Partial<Filters>) {
    this.filters = { ...this.filters, ...patch };
    this.schedulePreview();
  }
  private toggleTag(id: string) {
    const cur = this.filters.tag_ids ?? [];
    this.setF({ tag_ids: cur.includes(id) ? cur.filter((tid) => tid !== id) : [...cur, id] });
  }

  private schedulePreview() {
    clearTimeout(this.debounce);
    this.debounce = window.setTimeout(() => this.runPreview(), 300);
  }
  private async runPreview() {
    this.previewing = true;
    try { this.preview = await api.post<Preview>('/mailing/preview', { filters: this.filters }); }
    catch { this.preview = null; }
    finally { this.previewing = false; }
  }

  private async save() {
    if (!this.name.trim()) { showToast(t('mailing.toast.listNameRequired'), 'error'); return; }
    this.saving = true;
    const body = { name: this.name.trim(), filters: this.filters };
    try {
      if (this.selectedId) await api.put(`/mailing/lists/${this.selectedId}`, body);
      else {
        const created = await api.post<MailingList>('/mailing/lists', body);
        this.selectedId = created.id;
      }
      await this.reloadLists();
      showToast(t('mailing.toast.listSaved'), 'success');
    } catch (e) { showToast(e instanceof ApiError ? e.message : t('mailing.toast.listSaveFailed'), 'error'); }
    finally { this.saving = false; }
  }
  private async reloadLists() { this.lists = await api.get<MailingList[]>('/mailing/lists').catch(() => this.lists); }
  private async del() {
    if (!this.selectedId) return;
    if (!confirm(t('mailing.toast.confirmDeleteList', { name: this.name }))) return;
    try { await api.del(`/mailing/lists/${this.selectedId}`); this.newList(); await this.reloadLists(); showToast(t('mailing.toast.listDeleted'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }

  private opt(items: Named[], anyLabel: string) {
    return [{ value: '', label: anyLabel }, ...items.map((i) => ({ value: i.id, label: i.name }))];
  }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    const f = this.filters;
    const studentsIncluded = (f.contact_type ?? 'students') !== 'contacts';
    return html`
      <div style="padding-top:var(--space-4)" class="layout">
        <div>
          ${this.canManage ? html`<cadence-button full-width @click=${() => this.newList()}>${t('mailing.lists.newList')}</cadence-button>` : ''}
          <div class="lists" style="margin-top:10px">
            ${this.lists.map((l) => html`
              <button class="litem" aria-selected=${this.selectedId === l.id ? 'true' : 'false'} @click=${() => this.selectList(l)}>
                <div class="n">${l.name}</div>
                <div class="m">${l.created_by_name ? t('mailing.lists.byAuthor', { name: l.created_by_name }) : ''}</div>
              </button>`)}
            ${this.lists.length ? '' : html`<p class="muted">${t('mailing.lists.noSavedLists')}</p>`}
          </div>
        </div>

        <div class="builder">
          <cadence-card heading=${this.selectedId ? t('mailing.lists.editList') : t('mailing.lists.newList')}>
            <div class="row" style="margin-bottom:12px">
              <cadence-input label=${t('mailing.lists.listName')} placeholder=${t('mailing.lists.listNamePlaceholder')} .value=${this.name}
                @input=${(e: CustomEvent) => (this.name = e.detail)}></cadence-input>
            </div>

            <div class="fgrid">
              <div class="field">
                <cadence-select label=${t('mailing.lists.audience')} .value=${f.contact_type ?? 'students'} .options=${[
                  { value: 'students', label: t('mailing.lists.audienceStudents') }, { value: 'contacts', label: t('mailing.lists.audienceContacts') }, { value: 'both', label: t('mailing.lists.audienceBoth') },
                ]} @change=${(e: CustomEvent) => this.setF({ contact_type: e.detail })}></cadence-select>
              </div>

              ${studentsIncluded ? html`
                <div class="field">
                  <cadence-select label=${t('mailing.lists.studentStatus')} .value=${f.status ?? ''} .options=${[
                    { value: '', label: t('mailing.lists.any') }, { value: 'active', label: t('mailing.lists.active') }, { value: 'inactive', label: t('mailing.lists.inactive') },
                  ]} @change=${(e: CustomEvent) => this.setF({ status: e.detail || undefined })}></cadence-select>
                </div>
                ${this.classes.length ? html`<div class="field"><cadence-select label=${t('mailing.lists.enrolledClass')} .value=${f.class_id ?? ''}
                  .options=${this.opt(this.classes, t('mailing.lists.anyClass'))} @change=${(e: CustomEvent) => this.setF({ class_id: e.detail || undefined })}></cadence-select></div>` : ''}
                ${this.danceTypes.length ? html`<div class="field"><cadence-select label=${t('mailing.lists.danceType')} .value=${f.dance_type_id ?? ''}
                  .options=${this.opt(this.danceTypes, t('mailing.lists.anyDanceType'))} @change=${(e: CustomEvent) => this.setF({ dance_type_id: e.detail || undefined })}></cadence-select></div>` : ''}
                ${this.levels.length ? html`<div class="field"><cadence-select label=${t('mailing.lists.level')} .value=${f.level_id ?? ''}
                  .options=${this.opt(this.levels, t('mailing.lists.anyLevel'))} @change=${(e: CustomEvent) => this.setF({ level_id: e.detail || undefined })}></cadence-select></div>` : ''}
                <div class="field">
                  <cadence-select label=${t('mailing.lists.paymentStatus')} .value=${f.payment_status ?? ''} .options=${[
                    { value: '', label: t('mailing.lists.any') }, { value: 'paid_up', label: t('mailing.lists.paidUp') }, { value: 'overdue', label: t('mailing.lists.hasOverdue') }, { value: 'pending', label: t('mailing.lists.hasPending') },
                  ]} @change=${(e: CustomEvent) => this.setF({ payment_status: e.detail || undefined })}></cadence-select>
                </div>
                ${this.events.length ? html`<div class="field"><cadence-select label=${t('mailing.lists.eventParticipant')} .value=${f.event_id ?? ''}
                  .options=${this.opt(this.events, t('mailing.lists.anyEvent'))} @change=${(e: CustomEvent) => this.setF({ event_id: e.detail || undefined })}></cadence-select></div>` : ''}
                <div class="field"><cadence-input label=${t('mailing.lists.minAge')} type="number" placeholder=${t('mailing.lists.minAgePlaceholder')}
                  .value=${String(f.age_min ?? '')} @input=${(e: CustomEvent) => this.setF({ age_min: e.detail })}></cadence-input></div>
                <div class="field"><cadence-input label=${t('mailing.lists.maxAge')} type="number" placeholder=${t('mailing.lists.maxAgePlaceholder')}
                  .value=${String(f.age_max ?? '')} @input=${(e: CustomEvent) => this.setF({ age_max: e.detail })}></cadence-input></div>
              ` : html`<div class="field"><p class="muted">${t('mailing.lists.contactsTagsOnly')}</p></div>`}
            </div>

            ${this.tags.length ? html`<div class="field" style="margin-top:12px">
              <label>${studentsIncluded ? t('mailing.lists.tagsLabelBoth') : t('mailing.lists.tagsLabelContacts')}</label>
              <div class="tagpick">
                ${this.tags.map((tag) => html`<button type="button" class="tagtoggle" aria-pressed=${(f.tag_ids ?? []).includes(tag.id) ? 'true' : 'false'}
                  @click=${() => this.toggleTag(tag.id)}><span class="dot" style="background:${tag.color}"></span>${tag.name}</button>`)}
              </div>
            </div>` : ''}

            ${this.canManage ? html`<div class="actions" style="margin-top:16px">
              <cadence-button ?loading=${this.saving} @click=${() => this.save()}>${this.selectedId ? t('mailing.lists.saveChanges') : t('mailing.lists.saveList')}</cadence-button>
              ${this.selectedId ? html`<button class="rm" @click=${() => this.del()}>${t('mailing.lists.deleteList')}</button>` : ''}
            </div>` : ''}
          </cadence-card>

          <div class="preview">
            <div class="ph">
              <span class="count">${this.previewing ? t('mailing.lists.resolving') : ((this.preview?.total ?? 0) === 1 ? t('mailing.lists.recipientCount_one', { count: this.preview?.total ?? 0 }) : t('mailing.lists.recipientCount_other', { count: this.preview?.total ?? 0 }))}</span>
              ${this.preview ? html`<span class="muted">${t('mailing.lists.canBeEmailed', { count: this.preview.subscribed })}${this.preview.total > this.preview.sample.length ? t('mailing.lists.showingFirst', { count: this.preview.sample.length }) : ''}</span>` : ''}
            </div>
            <div class="rows">
              ${(this.preview?.sample ?? []).map((r) => html`<div class="r">
                <div class="who"><div>${r.name}</div><div class="em">${r.email}</div></div>
                <div style="display:flex;gap:6px;align-items:center">
                  ${!r.subscribed ? html`<span class="pill unsub">${t('mailing.lists.unsubscribedPill')}</span>` : ''}
                  <span class="pill">${r.type}</span>
                </div>
              </div>`)}
              ${this.preview && this.preview.total === 0 ? html`<div class="r"><span class="muted">${t('mailing.lists.noRecipients')}</span></div>` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
